import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { BottomNavigationBar, NavigationBarConfig } from '../components/BottomNavigationBar';

interface NavigationContextValue {
  isVisible: boolean;
  config: NavigationBarConfig;
  showNavigation: (config?: NavigationBarConfig) => void;
  hideNavigation: () => void;
  updateConfig: (config: Partial<NavigationBarConfig>) => void;
}

const NavigationContext = createContext<NavigationContextValue | undefined>(undefined);

export interface NavigationProviderProps {
  children: ReactNode;
  defaultConfig?: NavigationBarConfig;
  defaultVisible?: boolean;
}

/**
 * Navigation Provider
 * Wrap your app with this provider to enable bottom navigation bar
 */
export function NavigationProvider({
  children,
  defaultConfig = {},
  defaultVisible = false,
}: NavigationProviderProps) {
  const [isVisible, setIsVisible] = useState(defaultVisible);
  const [config, setConfig] = useState<NavigationBarConfig>(defaultConfig);

  const showNavigation = useCallback((newConfig?: NavigationBarConfig) => {
    if (newConfig) {
      setConfig(newConfig);
    }
    setIsVisible(true);
  }, []);

  const hideNavigation = useCallback(() => {
    setIsVisible(false);
  }, []);

  const updateConfig = useCallback((newConfig: Partial<NavigationBarConfig>) => {
    setConfig((prev) => ({ ...prev, ...newConfig }));
  }, []);

  return (
    <NavigationContext.Provider
      value={{
        isVisible,
        config,
        showNavigation,
        hideNavigation,
        updateConfig,
      }}
    >
      {children}
      {isVisible && <BottomNavigationBar {...config} />}
    </NavigationContext.Provider>
  );
}

/**
 * Hook to control navigation bar from any component
 *
 * @example
 * ```tsx
 * import { useNavigation } from '@room/shared-ui';
 *
 * function MyPage() {
 *   const { showNavigation } = useNavigation();
 *
 *   useEffect(() => {
 *     // Show navigation bar when component mounts
 *     showNavigation({
 *       showHomeButton: true,
 *       showRefreshButton: true,
 *     });
 *   }, [showNavigation]);
 *
 *   return <div>My Page Content</div>;
 * }
 * ```
 */
export function useNavigation() {
  const context = useContext(NavigationContext);

  if (!context) {
    throw new Error('useNavigation must be used within NavigationProvider');
  }

  return context;
}

/**
 * Higher-order component to automatically show navigation bar
 *
 * @example
 * ```tsx
 * import { withNavigation } from '@room/shared-ui';
 *
 * function MyPage() {
 *   return <div>My Page Content</div>;
 * }
 *
 * export default withNavigation(MyPage, {
 *   showHomeButton: true,
 *   showRefreshButton: true,
 * });
 * ```
 */
export function withNavigation<P extends object>(
  Component: React.ComponentType<P>,
  config?: NavigationBarConfig
) {
  return function WithNavigationComponent(props: P) {
    const { showNavigation, hideNavigation } = useNavigation();

    // Show navigation on mount, hide on unmount
    useState(() => {
      showNavigation(config);
      return () => hideNavigation();
    });

    return <Component {...props} />;
  };
}
